import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProjects } from "../lib/ProjectContext";
import { api, ApiError } from "../lib/apiClient";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import type { ChatThread } from "@shared/types";

/**
 * The Rail sidebar only ever shows the 5 most recent chats (compact nav,
 * not a full list) — this is the "view all" escape hatch to the rest of
 * what GET /api/projects/:id/ask-history already keeps server-side (up to
 * the 50 most recent threads for this user on this project). Nothing here
 * was actually being deleted; it just had no way to be reached once it
 * scrolled off the sidebar's 5-item list.
 */
export function ChatHistoryPage() {
  const { selectedProjectId, projects, loading: projectsLoading } = useProjects();
  const navigate = useNavigate();
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedProjectId) return;
    setLoading(true);
    setError(null);
    api
      .get<{ threads: ChatThread[] }>(`/api/projects/${selectedProjectId}/ask-history`)
      .then((data) => setThreads(data.threads))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load chat history."))
      .finally(() => setLoading(false));
  }, [selectedProjectId]);

  function openThread(thread: ChatThread) {
    navigate("/ask", { state: { restoreThread: { threadId: thread.threadId, turns: thread.turns } } });
  }

  if (projectsLoading) return <p className="muted">Loading…</p>;
  if (projects.length === 0) {
    return (
      <div className="card">
        <p>No projects yet. Ask an owner to create one, or (if you're the owner) use the Sources screen to get started.</p>
      </div>
    );
  }
  if (loading) return <p className="muted">Loading chat history…</p>;

  return (
    <div className="content-col-wide">
      <div className="sources-head">
        <p className="sources-desc" style={{ marginTop: 0 }}>
          Every question you've asked on this project, most recent first (up to the last 50). Select one to reopen
          it, follow-ups and all, in Ask.
        </p>
      </div>

      {error && <p className="error-text">{error}</p>}

      {threads.length === 0 ? (
        <div className="card">
          <p className="muted">Nothing asked yet on this project.</p>
        </div>
      ) : (
        threads.map((thread) => {
          const firstQuestion = thread.turns[0]?.question ?? "(no question)";
          return (
            <button key={thread.threadId} className="card chat-history-row" type="button" onClick={() => openThread(thread)}>
              <span className="chat-history-question">{firstQuestion}</span>
              <span className="chat-history-meta">
                {thread.turns.length > 1 ? `${thread.turns.length} turns · ` : ""}
                {formatRelativeTime(thread.cachedAt)}
              </span>
            </button>
          );
        })
      )}
    </div>
  );
}
